import React, { useCallback, useState } from 'react';
import { Button, Checkbox } from '@gafe/common-ui';
import merge from 'classnames';

import { RequestStatusBar } from '@/components/generic/RequestStatusBar';
import { ContainerWithCurtain } from '@/components/layout/ContainerWithCurtain';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/proxy/reactstrap/Modal';

import { Props } from './types';

import styles from './AcceptLeadsPassingModalV2.module.scss';

export const AcceptLeadsPassingModal = (props: Props) => {
  const { isOpened, companyId, acceptProgram, acceptingProgram } = props;
  const [checked, setChecked] = useState(false);

  const closeModal = useCallback(
    () => !acceptingProgram && props.closeModal(),
    [acceptingProgram, props],
  );

  const onAcceptClick = useCallback(() => {
    if (companyId) {
      acceptProgram(companyId);
    }
  }, [acceptProgram, companyId]);

  const textWithTopGutter = merge(styles.text, styles.gutter_top);

  if (!companyId) return null;

  return (
    <Modal
      centered
      confirmation
      fullscreen
      id="accept-lead-agreement-modal"
      isOpen={isOpened}
      wrapClassName={styles.modal}
    >
      <ModalHeader toggle={closeModal}>
        Please read these terms regarding our lead passing agreement:
      </ModalHeader>

      <ModalBody>
        <ContainerWithCurtain
          className={styles.container}
          curtainType="blank"
          showCurtain={acceptingProgram}
          transparent
        >
          <div className={styles.copy}>
            <h3 className={styles.title}>LEADS AGREEMENT</h3>
            <p className={textWithTopGutter}>Last updated: October 22, 2020</p>
            <p className={textWithTopGutter}>
              This Leads Agreement (this <b>“Agreement”</b>) is a legal agreement between your
              business (<b>“you”</b>) and GAF Energy LLC (<b>“GAF Energy,” “we,” “us”</b> or{' '}
              <b>“our”</b>) regarding leads identified for, and provided to, you by GAF Energy or
              its affiliates (<b>“Leads”</b>). If you are accepting this Agreement on behalf of an
              entity, you represent and warrant that you have authority to bind that entity to this
              Agreement and by accepting this Agreement, you are doing so on behalf of that entity
              (and all references to “you” in this Agreement refer to that entity). Your receipt and
              use of Leads (such as by attempting to contact a Lead) is subject to this Agreement as
              it may be amended from time to time, and you also accept the GAF Energy Terms of Use
              found at{' '}
              <a
                className={styles.link}
                href=" https://portal.gaf.energy/terms-of-service/"
                rel="noopener noreferrer"
                target="_blank"
              >
                https://portal.gaf.energy/terms-of-service/
              </a>{' '}
              (the
              <b>“Terms of Use”</b>) and agree to our handling of data as described in GAF Energy’s
              Privacy Policy found at{' '}
              <a
                className={styles.link}
                href="https://portal.gaf.energy/privacy-policy/"
                rel="noopener noreferrer"
                target="_blank"
              >
                https://portal.gaf.energy/privacy-policy/
              </a>{' '}
              (the <b>“Privacy Policy”</b>) as they may be amended from time to time. In the event
              of any conflict between this Agreement and any other applicable terms, this Agreement
              shall control. It is important that you carefully read and understand the terms and
              conditions of this Agreement, the Terms of Use, and the Privacy Policy. By clicking
              the “I Accept” button, or any similar mechanism, or by using any Leads (such as by
              attempting to contact a Lead), you agree to be bound by and comply with the terms and
              conditions of this Agreement and the Terms of Use. This Agreement will be effective as
              of the earlier of the date you click the “I Accept” button, or any similar mechanism,
              and the date that you first use any Leads (such as by attempting to contact a Lead)
              (the <b>“Effective Date”</b>). IF YOU DO NOT AGREE TO ALL OF THE TERMS AND CONDITIONS
              OF THIS AGREEMENT, THE TERMS OF USE, AND THE PRIVACY POLICY, DO NOT CLICK THE “I
              ACCEPT” BUTTON, OR OTHER SIMILAR MECHANISM, AND DO NOT USE LEADS.
            </p>
            <p className={textWithTopGutter}>
              From time to time, we may update this Agreement by posting the updated version on{' '}
              <a
                className={styles.link}
                href="https://portal.gaf.energy/terms-of-service/"
                rel="noopener noreferrer"
                target="_blank"
              >
                https://portal.gaf.energy/terms-of-service/
              </a>{' '}
              (such as via a link to it in the footer) and updating the “Last updated” date at the
              top of this page. Unless you reject the update by emailing us at support@gaf.energy
              with a notice of termination of this Agreement prior to any of the following, you will
              be deemed to accept the changes and they will take effect at the earlier of:
            </p>
            <ul className={textWithTopGutter} style={{ marginLeft: '3rem', listStyleType: 'disc' }}>
              <li>12:00 p.m. Eastern time on the 10th day after we post them; or</li>
              <li>
                your acceptance of the updated Agreement via a click-through process or some other
                method that we specify.
              </li>
            </ul>
            <p className={textWithTopGutter}>
              You agree to review this Agreement periodically to ensure that you are familiar with
              the most recent version.
            </p>
            <p className={textWithTopGutter}>
              <b>
                Notice Regarding Dispute Resolution: This Agreement contains provisions that govern
                how claims GAF Energy and you have against each other are resolved (see Section 3
                (Release and Indemnification), Section 4 (Disclaimer of Warranty), Section 5
                (Limitation of Liability), and Section 9 (Miscellaneous) below).
              </b>
            </p>
            <p className={textWithTopGutter}>
              1.<span className={styles.underline_text}>Leads and General Obligations.</span>
            </p>
            <p className={textWithTopGutter}>
              1.1 Eligibility Requirements. To be eligible to receive and use Leads, you must now,
              and throughout the term of this Agreement comply with the additional obligations set
              forth in any program documents issued by GAF Energy from time to time with respect to
              Leads.
            </p>
            <p className={textWithTopGutter}>
              {' '}
              1.2 License. GAF Energy has, and shall have, all right, title, and interest in and to
              all Leads, and except for the limited license granted herein, nothing contained in
              this Agreement shall be construed to convey to you any right, title, or interest in or
              to any Leads. Subject to the terms and conditions in this Agreement, GAF Energy grants
              you a limited, revocable, non-exclusive, non-transferable license, without the right
              to sublicense, during the term of this Agreement (and, unless you have breached this
              Agreement, thereafter), to use the Leads solely for the purpose of, and only to the
              extent necessary for, your own lawful activities to (a) sell your services to the
              individuals identified in the Leads or the entities they represent; and (b) to manage
              your relationship with them. You shall not “sell” Leads to third parties (as such term
              is defined in the California Consumer Privacy Act (<b>“CCPA”</b>) or otherwise), and
              the limited rights granted to you under this Section with respect to the Leads may not
              be sold, resold, assigned, leased, rented, sublicensed, or otherwise transferred, or
              made available for use by third parties, in whole or in part, by you without GAF
              Energy’s prior written consent in each instance. Any use by you of Leads not
              specifically authorized under this Agreement is prohibited. All rights related to the
              Leads that are not expressly granted to you under this Section 1.2 are reserved by GAF
              Energy. Without limiting the foregoing, GAF Energy may collect, retain, use, disclose,
              and otherwise process Personal Information (as defined below) about Leads in any
              lawful manner.
            </p>
            <p className={textWithTopGutter}>
              1.3 No Minimum Commitment. GAF Energy makes no guarantee that you will receive any
              specific number of Leads. You acknowledge that there are no minimum commitments by GAF
              Energy with respect to the number of Leads you will receive in connection with this
              Agreement and you further acknowledge that it is possible that you may not receive any
              Leads in connection herewith.
            </p>
            <p className={textWithTopGutter}>
              {' '}
              1.4 Your Personnel. You may grant access to Leads to your employees, contractors,
              agents, and other representatives (<b>“your Personnel”</b>). You shall cause your
              Personnel to comply with this Agreement and the Terms of Use and all other terms and
              conditions applicable to the Leads. You shall be solely responsible for the actions
              and omissions of your Personnel.
            </p>
            <p className={textWithTopGutter}>
              1.5 Data Protection Laws. You shall comply, and shall cause all your Personnel to
              comply, at all times with all laws relevant or applicable to your use of Leads,
              including, without limitation, all Data Protection Laws applicable to the collection
              and use of Personal Information. <b>“Personal Information”</b> means information (a)
              that identifies, or can be used to contact, an individual or device; (b) with respect
              to which there is a reasonable basis to believe the information can be used to
              identify or contact an individual or device; (c) that relates to the individual or
              device that is identifiable as described in (a) or (b) above; or (d) that is
              considered “personally identifiable information,” “personal information,” “personal
              data,” “nonpublic personal information,” “protected health information,” or the like
              by applicable Data Protection Laws; and <b>“Data Protection Laws”</b> mean all laws,
              rules, regulations, self-regulatory programs and guidance, and any related published
              clarifications, guidance, interpretations, staff reports, and recommendations by any
              applicable governmental authority in relation to: (i) data protection; (ii) privacy;
              (iii) interception and monitoring of communications; (iv) initiating communications by
              email, telephone, text message or other means; (v) restrictions on, or requirements in
              respect of, the processing of Personal Information of any kind; and (vi) actions
              required to be taken in respect of unauthorized or accidental access to or use or
              disclosure of Personal Information, including, without limitation, any such Data
              Protection Law in modified or supplemented form and any newly adopted Data Protection
              Law replacing a previous Data Protection Law. Without limiting the foregoing, Personal
              Information includes Leads and data about individual Leads, and Data Protection Laws
              include the CCPA, the Telephone Consumer Protection Act and associated regulations
              issued by the Federal Communications Commission, the Federal Trade Commission’s
              Telemarketing Sales Rule, CAN-SPAM, and all state laws regulating marketing and
              electronic or telephonic communications.
            </p>
            <p className={textWithTopGutter}>
              1.6 Do Not Contact. Without limiting the obligations in Section 1.5, you shall (a)
              immediately honor any request by any Lead not to be contacted for marketing purposes
              again, regardless of whether the Lead makes that request to you directly or via GAF
              Energy; and (b) refrain from using any automated equipment, such as a predictive
              dialer or autodialer, to call or text any Lead.
            </p>
            <p className={textWithTopGutter}>
              1.7 Contacting Leads. You will contact any Lead within two business days of receipt of
              such Lead unless the Lead or GAF Energy has specified otherwise. Your failure to
              contact a Lead within such time period may result in you receiving fewer Leads.
            </p>
            <p className={textWithTopGutter}>
              2.<span className={styles.underline_text}>No Use for Children Under 13</span>. You
              hereby affirm that you are over the age of 13, as Leads is not intended for children
              under 13. You further affirm that you are fully able and competent to enter into the
              terms, conditions, obligations, affirmations, representations, and warranties set
              forth in this Agreement and to abide by and comply with this Agreement. If the
              affirmations in this Section 2 are not true, then you may not use Leads.
            </p>
            <p className={textWithTopGutter}>
              3.<span className={styles.underline_text}>Release and Indemnification</span>. You, on
              your own behalf and on behalf of your Personnel, hereby release and discharge GAF
              Energy, its affiliates, each licensor of any portion of Leads, and each of their
              respective officers, directors, shareholders, members, employees, agents,
              subcontractors, successors, and assigns (collectively, the <b>“GAF Energy Parties”</b>
              ), and agree that your Personnel and you shall waive and not assert any action or
              claim against any of the GAF Energy Parties for, any damages arising directly or
              indirectly out of, related to, or in connection with (a) your business operations and
              your Personnel’s and your acts and omissions, whether in connection with your
              businesses, Leads, or otherwise, including, but not limited to, any access (whether
              physically, electronically, or otherwise) to or use of any Leads; (b) your receipt and
              use of Leads; (c) all Leads; and (d) the acts and omissions of the individuals
              identified in the Leads and their representatives and the entities they represent. You
              shall immediately notify GAF Energy of and indemnify and hold harmless the GAF Energy
              Parties from and against any and all liabilities, damages, judgments, awards,
              settlement, costs, expenses, and other charges (including, but not limited to,
              reasonable fees and expenses of attorneys) suffered or incurred by any of the GAF
              Energy Parties arising directly or indirectly out of, related to, or in connection
              with any claim, demand, action, lawsuit, or any other legal action brought by your
              Personnel, you, a property owner or its representatives, or any other party against
              any of the GAF Energy Parties arising directly or indirectly out of, related to, or in
              connection with (i) your business operations and your Personnel’s and your acts and
              omissions, whether in connection with your businesses, Leads, or otherwise, including,
              but not limited to, any access (whether physically, electronically, or otherwise) to
              or use of any Leads; (ii) your receipt and use of Leads; (iii) all Leads; (iv) the
              acts and omissions of the individuals identified in the Leads and their
              representatives and the entities they represent; (v) your and your Personnel’s breach
              of this Agreement or the Terms of Use; and (vi) your and your Personnel’s breach of
              any all laws relevant or applicable to your businesses, your receipt and use of Leads,
              and your other obligations under the Agreement, including, without limitation, all
              Data Protection Laws.
            </p>
            <p className={textWithTopGutter}>
              4.<span className={styles.underline_text}>DISCLAIMER OF WARRANTY</span>. THE GAF
              ENERGY PARTIES MAKE NO REPRESENTATIONS OR WARRANTIES WITH RESPECT TO LEADS. THE GAF
              ENERGY PARTIES HEREBY DISCLAIM ALL WARRANTIES WITH RESPECT TO LEADS, EXPRESS OR
              IMPLIED, INCLUDING, WITHOUT LIMITATION, ANY WARRANTIES OF MERCHANTABILITY,
              NON-INFRINGEMENT, OR FITNESS FOR A PARTICULAR USE OR PURPOSE. THE GAF ENERGY PARTIES
              DO NOT WARRANT THAT LEADS WILL MEET YOUR REQUIREMENTS IN ANY RESPECT, THAT GAF
              ENERGY’S PROVISION OF LEADS WILL BE UNINTERRUPTED OR ERROR FREE, THAT ERRORS IN LEADS
              CAN OR WILL BE CORRECTED, OR THAT LEADS, OR INFORMATION OTHERWISE DERIVED FROM YOUR
              USE OF LEADS, WILL BE ACCURATE, COMPLETE, OR RELIABLE. YOU FURTHER UNDERSTAND AND
              ACKNOWLEDGE THAT YOU ARE SOLELY RESPONSIBLE FOR ANY DECISIONS YOU MAKE BASED ON YOUR
              USE OF LEADS. INFORMATION TRANSMITTED OVER THE INTERNET OR STORED IN SYSTEMS REACHABLE
              FROM THE INTERNET IS INHERENTLY INSECURE, AND THE GAF ENERGY PARTIES DO NOT MAKE ANY
              WARRANTY WITH RESPECT TO THE SECURITY OF THE LEADS TRANSMITTED TO YOU BY GAF ENERGY,
              WHETHER SUCH LEADS ARE TRANSMITTED TO YOUR MOBILE DEVICE OR NETWORK, OR OTHERWISE. THE
              GAF ENERGY PARTIES DO NOT MAKE ANY WARRANTY THAT LEADS WILL BE FREE FROM VIRUSES,
              HARMFUL CODE, OR ANY OTHER UNAUTHORIZED ACCESS, AND NO GAF ENERGY PARTY WILL HAVE ANY
              RESPONSIBILITY OR LIABILITY IN CONNECTION WITH ANY SUCH UNAUTHORIZED ACCESS. NO GAF
              ENERGY PARTY SHALL HAVE RESPONSIBILITY OR LIABILITY FOR ANY LOSS CAUSED BY, ARISING
              DIRECTLY OR INDIRECTLY OUT OF, OR RESULTING TO YOUR MOBILE DEVICE OR NETWORK. WITHOUT
              LIMITING THE FOREGOING, NO GAF ENERGY PARTY WILL HAVE ANY LIABILITY TO YOU IN RELATION
              TO LEADS. YOU ACKNOWLEDGE THAT THE PROVISIONS OF THIS SECTION ARE A MATERIAL
              INDUCEMENT AND CONSIDERATION TO GAF ENERGY TO GRANT THE RIGHTS CONTAINED IN THIS
              AGREEMENT AND TO PROVIDE YOU WITH ACCESS TO LEADS.
            </p>
            <p className={textWithTopGutter}>
              5.<span className={styles.underline_text}>LIMITATION OF LIABILITY</span>. TO THE
              MAXIMUM EXTENT PERMITTED BY LAW, IN ADDITION TO THE RELEASE, THE WARRANTY DISCLAIMER,
              AND OTHER DISCLAIMERS IN THIS AGREEMENT, IN NO EVENT SHALL (A) ANY GAF ENERGY PARTY BE
              LIABLE FOR ANY INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL, PUNITIVE DAMAGES, LOST
              REVENUE, LOST PROFITS, OR LOST OR DAMAGED DATA ARISING DIRECTLY OR INDIRECTLY OUT OF,
              RELATED TO, OR IN CONNECTION WITH THIS AGREEMENT, INCLUDING, WITHOUT LIMITATION, YOUR
              RECEIPT AND USE OF LEADS, EVEN IF A GAF ENERGY PARTY HAS BEEN ADVISED OF THE
              POSSIBILITY OF SUCH DAMAGES; AND (B) THE TOTAL AGGREGATE LIABILITY OF ALL GAF ENERGY
              PARTIES FOR ANY DIRECT DAMAGES ARISING DIRECTLY OR INDIRECTLY OUT OF, RELATED TO, OR
              IN CONNECTION WITH THIS AGREEMENT, INCLUDING, WITHOUT LIMITATION, YOUR RECEIPT AND USE
              OF LEADS, EXCEED $100. THE FOREGOING LIMITATIONS WILL APPLY NOTWITHSTANDING THE
              FAILURE OF ESSENTIAL PURPOSES OF ANY LIMITED REMEDY OF ANY KIND AND REGARDLESS OF THE
              FORM OF ACTION, WHETHER IN CONTRACT, TORT, OR ANY OTHER LEGAL THEORY.
            </p>
            <p className={textWithTopGutter}>
              6.<span className={styles.underline_text}>Exclusions</span>. Some jurisdictions do not
              allow the exclusion of certain warranties or the limitation or exclusion of liability
              for certain damages. Accordingly, some of the above disclaimers, limitations, and
              exclusions may not apply to you. To the extent that a GAF Energy Party may not, as a
              matter of applicable law, disclaim any warranty, or limit or exclude any liability,
              the scope and duration of such warranty and the extent of the GAF Energy Party’s
              liability shall be the minimum permitted under such applicable law.
            </p>
            <p className={textWithTopGutter}>
              7.<span className={styles.underline_text}>Termination</span>. This Agreement is
              effective upon the Effective Date and shall continue until it is terminated as
              provided herein. GAF Energy may immediately terminate this Agreement and/or your
              receipt and use of Leads, at any time and for any reason, with or without cause, with
              or without notice, and without cost or penalty of any kind. GAF Energy reserves the
              right, at any time, to modify or discontinue its provision of Leads to you, with or
              without notice. You agree that GAF Energy will not be liable to you or to any third
              party for any modification or discontinuance of the provision by GAF Energy of Leads.
              This Agreement will automatically terminate upon your breach of any of the terms of
              this Agreement. Upon termination of this Agreement, all rights granted hereunder to
              you shall immediately terminate, and except as set forth in Section 1.2, you must
              cease all use of Leads. However, all other provisions of the Agreement shall survive
              such termination.
            </p>
            <p className={textWithTopGutter}>
              8.<span className={styles.underline_text}>No Agency</span>. You are not an employee,
              agent, or representative of GAF Energy, or GAF Energy’s affiliates, and your use of
              Leads does not create any such relationship between GAF Energy or any of GAF Energy’s
              affiliates and you. Under no circumstances shall you, your Personnel, or any other
              individual or entity associated in any manner with you be deemed to be the agents,
              salespeople, employees, representatives, or partners of, or joint venturers with, GAF
              Energy or any of GAF Energy’s affiliates. You shall be solely responsible for the
              payment or reimbursement of all expenses of your own agents, salespeople, and
              employees. You shall have no right to enter into any contract or commitment in the
              name, or on behalf, of GAF Energy or any of GAF Energy’s affiliates, and you shall not
              do any act, or omit to do any act, that might result in any third party believing that
              you or your Personnel have the power to contract or incur any commitment on behalf of
              GAF Energy or any of GAF Energy’s affiliates. Without GAF Energy’s prior written
              consent in each instance, or except as expressly authorized to do so in this
              Agreement, your Personnel and you are not authorized to and shall not (a) create any
              obligations on behalf of GAF Energy or any of GAF Energy’s affiliates, or (b) make any
              representations or warranties on behalf of GAF Energy or any of GAF Energy’s
              affiliates, whether with respect to Leads, GAF Energy’s and its affiliates’ products
              and services, or otherwise.
            </p>
            <p className={textWithTopGutter}>
              9.<span className={styles.underline_text}>Miscellaneous</span>. This Agreement,
              together with the Terms of Use, is the entire agreement between you and GAF Energy
              with respect to, and supersedes any previous oral or written communications or
              documents concerning the subject matter of this Agreement. For the avoidance of doubt,
              this Agreement does not supersede or amend any agreement between GAF Energy and you
              with respect to your participation in any of GAF Energy’s roofing contractor programs.
              This Agreement is governed by the laws of the State of Delaware, USA, without regard
              to conflicts of laws principles. You agree that any dispute arising directly or
              indirectly out of, related to, or in connection with this Agreement shall be governed
              by the procedures set forth in the Terms of Use. This Agreement will not be governed
              by the U.N. Convention on Contracts for the International Sale of Goods. In the event
              any provision of this Agreement is found to be invalid or unenforceable, it will be
              enforced to the extent permissible and the remainder of this Agreement will remain in
              full force and effect. Failure by GAF Energy to prosecute any right with respect to a
              default hereunder will not constitute a waiver by GAF Energy or the right to enforce
              rights with respect to the same or any other breach. All GAF Energy Parties are
              intended third party beneficiaries of this Agreement and, upon your acceptance of this
              Agreement, all GAF Energy Parties will have the right (and will be deemed to have
              accepted the right) to enforce this Agreement against you as a third party
              beneficiary. Subject to the preceding sentence, this Agreement is between you and GAF
              Energy and there are no third party beneficiaries. You are responsible for compliance
              with all applicable laws and regulations in connection with your use of Leads, your
              presentations, and your business. Any heading, caption, or section title contained in
              this Agreement is inserted only as a matter of convenience and in no way defines or
              explains any section or provision hereof. You hereby agree to receive communications
              with respect to Leads by email, mobile push notification, and SMS/text notification.
              You agree that all agreements, notices, disclosures, and other communications that GAF
              Energy provides to you electronically (such as through email) satisfy any legal
              requirement that such communications be in writing.
            </p>
          </div>

          <Checkbox checked={checked} label="I accept" onClick={() => setChecked(!checked)} />
        </ContainerWithCurtain>

        <RequestStatusBar
          loading={acceptingProgram}
          messages={{ inProgress: 'Accepting Leads Passing Program' }}
        />
      </ModalBody>

      <ModalFooter confirmation>
        <Button
          colorSchema="neutral"
          disabled={acceptingProgram}
          onClick={closeModal}
          size="large"
          variant="text"
        >
          Cancel
        </Button>
        <Button
          colorSchema="destructive"
          disabled={!checked || acceptingProgram}
          onClick={onAcceptClick}
          size="large"
          variant="contained"
        >
          Accept
        </Button>
      </ModalFooter>
    </Modal>
  );
};
